import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { usePaystackPayment } from 'react-paystack';
import { useSearchParams } from 'react-router-dom';
import io from 'socket.io-client';
import { handleError, handleSuccess } from '../utils/errorHandler';
import { toast } from 'react-hot-toast';
import { apiGet, apiPost, apiPatch, API_ENDPOINTS, getFullImageUrl } from '../utils/api';
import { generatePaystackReference } from '../utils/paymentUtils';
import { useAuth } from '../contexts/AuthContext';

// ==========================================
// CONSTANTS
// ==========================================

const categories = [
  { value: '', label: 'All Categories' },
  { value: 'hair_products', label: 'Hair Products' },
  { value: 'nail_products', label: 'Nail Products' },
  { value: 'skin_care', label: 'Skin Care' },
  { value: 'tools', label: 'Tools & Equipment' },
  { value: 'accessories', label: 'Accessories' }
];

const serviceCategories = [
  { value: '', label: 'All Services' },
  { value: 'hair', label: 'Hair Services' },
  { value: 'nail', label: 'Nail Services' },
  { value: 'skin', label: 'Skin Care Services' },
  { value: 'massage', label: 'Massage Services' },
  { value: 'makeup', label: 'Makeup Services' },
  { value: 'other', label: 'Other Services' }
];

// ==========================================
// HELPER: Safe localStorage read
// ==========================================
function safeReadJSON(key, fallback) {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
}

// ==========================================
// HELPER: Sanitize customer input
// ==========================================
function sanitizeCustomerInfo(info) {
  return {
    name: (info.name || '').trim().slice(0, 100),
    email: (info.email || '').trim().slice(0, 255).toLowerCase(),
    phone: (info.phone || '').trim().slice(0, 20)
  };
}

// ==========================================
// HELPER: Shared booking fetch logic
// ==========================================
async function fetchBookingData(bookingNumberOrId, apiGetFn, byId = false) {
  const endpoint = byId
    ? `${API_ENDPOINTS.BOOKINGS}/${bookingNumberOrId}`
    : API_ENDPOINTS.BOOKINGS;
  const params = byId ? undefined : { booking_number: bookingNumberOrId };

  const response = await apiGetFn(endpoint, params);
  const booking = Array.isArray(response)
    ? (response.length > 0 ? response[0] : null)
    : response;

  if (!booking) return null;

  const allowedStatuses = ['scheduled', 'in-progress', 'completed'];
  if (!allowedStatuses.includes(booking.status)) {
    throw new Error(
      `Booking cannot be processed. Current status: ${booking.status}. Only bookings with status 'Scheduled', 'In Progress', or 'Completed' can be processed through POS.`
    );
  }

  return booking;
}

// ==========================================
// COMPONENT: POS
// ==========================================
const POS = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);

  // Initialize cart from localStorage
  const [cart, setCart] = useState(() => safeReadJSON('pos_cart', []));

  const [searchTerm, setSearchTerm] = useState('');
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedServiceCategory, setSelectedServiceCategory] = useState('');
  const [skuInput, setSkuInput] = useState('');
  const [scanningMode, setScanningMode] = useState(false);

  // Size Selection Modal State
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [selectedProductForSize, setSelectedProductForSize] = useState(null);

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [apiOnline, setApiOnline] = useState(true);
  const [bookingNumber, setBookingNumber] = useState('');

  // Initialize bookingData from localStorage
  const [bookingData, setBookingData] = useState(() => safeReadJSON('pos_booking_data', null));
  const [bookingLoading, setBookingLoading] = useState(false);

  // Initialize customerInfo from localStorage
  const [customerInfo, setCustomerInfo] = useState(() => {
    const saved = safeReadJSON('pos_customer_info', { name: '', email: '', phone: '' });
    return sanitizeCustomerInfo(saved);
  });

  // Persistence Effects
  useEffect(() => {
    localStorage.setItem('pos_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (bookingData) {
      localStorage.setItem('pos_booking_data', JSON.stringify(bookingData));
      if (!bookingNumber && bookingData.booking_number) {
        setBookingNumber(bookingData.booking_number);
      }
    } else {
      localStorage.removeItem('pos_booking_data');
    }
  }, [bookingData]);

  useEffect(() => {
    localStorage.setItem('pos_customer_info', JSON.stringify(customerInfo));
  }, [customerInfo]);

  const [activeTab, setActiveTab] = useState('products');
  const [miscCharges, setMiscCharges] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Pending bank-transfer / POS-terminal payment awaiting its receipt number
  const [receiptModal, setReceiptModal] = useState(null);

  // Receipt preview & transaction history state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnSearch, setTxnSearch] = useState('');

  // Refund state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundTxn, setRefundTxn] = useState(null);

  // Customer history state
  const [showCustomerHistory, setShowCustomerHistory] = useState(false);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [customerHistoryLoading, setCustomerHistoryLoading] = useState(false);

  // Granular loading states (Suggestion S3)
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [processingRefund, setProcessingRefund] = useState(false);

  // Ref to track mounted state for async effects
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Helper function to format prices without showing .00
  const formatPrice = (price) => {
    const formattedPrice = parseFloat(price).toFixed(2);
    if (formattedPrice.endsWith('.00')) {
      return formattedPrice.substring(0, formattedPrice.length - 3);
    }
    return formattedPrice;
  };

  // Memoize filtered products to improve performance
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.barcode && product.barcode.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = selectedCategory === '' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  // Memoize filtered services to improve performance
  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const matchesSearch = service.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
        (service.description && service.description.toLowerCase().includes(serviceSearchTerm.toLowerCase()));
      const matchesCategory = selectedServiceCategory === '' || service.category === selectedServiceCategory;
      return matchesSearch && matchesCategory;
    });
  }, [services, serviceSearchTerm, selectedServiceCategory]);

  useEffect(() => {
    fetchProducts();
    fetchServices();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await apiGet(API_ENDPOINTS.PUBLIC_SERVICES);
        if (!cancelled) setApiOnline(true);
      } catch {
        if (!cancelled) setApiOnline(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle URL parameters to automatically load bookings
  useEffect(() => {
    const bookingId = searchParams.get('booking_id');
    const customerId = searchParams.get('customer_id');

    if (bookingId) {
      fetchBookingById(bookingId);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('booking_id');
      newSearchParams.delete('customer_id');
      setSearchParams(newSearchParams);
    }
  }, [searchParams]);

  // ==========================================
  // BOOKING LOGIC (consolidated, fix #2)
  // ==========================================
  const processBookingData = useCallback(async (booking) => {
    if (!booking) return;

    setBookingData(booking);
    setBookingNumber(booking.booking_number || '');
    setCustomerInfo(sanitizeCustomerInfo({
      name: booking.customer_name || '',
      email: booking.customer_email || '',
      phone: booking.customer_phone || ''
    }));

    // Auto-update status to 'in-progress' for walk-in customers
    const isWalkIn = booking.customer_type === 'walk_in' || booking.customer_type === 'walk-in';
    if (isWalkIn && booking.status === 'scheduled') {
      try {
        await apiPatch(`${API_ENDPOINTS.BOOKINGS}/${booking.id}`, {
          status: 'in-progress',
          updated_at: new Date().toISOString()
        });
        setBookingData({ ...booking, status: 'in-progress' });
        handleSuccess(`Walk-in customer ${booking.customer_name} is now in service! Service: ${booking.service_names ? booking.service_names.join(', ') : 'N/A'}`);
      } catch (updateError) {
        console.error('Error updating booking status:', updateError);
        handleSuccess(`Booking loaded! Customer: ${booking.customer_name}, Service: ${booking.service_names ? booking.service_names.join(', ') : 'N/A'}`);
      }
    } else {
      handleSuccess(`Booking loaded! Customer: ${booking.customer_name}, Service: ${booking.service_names ? booking.service_names.join(', ') : 'N/A'}`);
    }
  }, []);

  const fetchBookingById = useCallback(async (bookingId) => {
    try {
      setBookingLoading(true);
      const booking = await fetchBookingData(bookingId, apiGet, true);
      if (booking) {
        await processBookingData(booking);
      }
    } catch (error) {
      console.error('Error fetching booking by ID:', error);
      handleError(null, error.message || 'Error loading booking. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  }, [processBookingData]);

  const fetchBooking = useCallback(async () => {
    if (!bookingNumber.trim()) {
      handleError(null, 'Please enter a booking number');
      return;
    }
    setBookingLoading(true);
    try {
      const booking = await fetchBookingData(bookingNumber, apiGet, false);
      if (booking) {
        await processBookingData(booking);
      } else {
        handleError(null, 'Booking not found');
        setBookingData(null);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      handleError(null, error.message || 'Error fetching booking. Please try again.');
      setBookingData(null);
    } finally {
      setBookingLoading(false);
    }
  }, [bookingNumber, processBookingData]);

  const fetchProducts = useCallback(async () => {
    let cancelled = false;
    try {
      const response = await apiGet(API_ENDPOINTS.INVENTORY);
      if (!cancelled) {
        setProducts(response.filter(product => product.stock_level > 0));
      }
    } catch (error) {
      if (!cancelled) {
        console.error('Error fetching products:', error);
        handleError(error, 'Failed to fetch products.');
      }
    } finally {
      if (!cancelled) setLoading(false);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    let cancelled = false;
    try {
      const response = await apiGet(API_ENDPOINTS.SERVICES);
      if (!cancelled) setServices(response);
    } catch (error) {
      if (!cancelled) {
        console.error('Error fetching services:', error);
        handleError(error, 'Failed to fetch services.');
      }
    }
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setAppliedCoupon(null);
    setCouponCode('');
    setPaymentConfirmed(false);
    setMiscCharges([]);
  }, []);

  const clearBooking = useCallback(() => {
    setBookingData(null);
    setBookingNumber('');
    setCustomerInfo({ name: '', email: '', phone: '' });
    setPaymentConfirmed(false);
    setMiscCharges([]);
  }, []);

  // ==========================================
  // CART MANAGEMENT LOGIC
  // ==========================================

  const getSubtotal = useCallback(() => {
    const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    const serviceTotal = bookingData ? parseFloat(bookingData.service_price || 0) : 0;
    const miscTotal = miscCharges.reduce((sum, charge) => sum + parseFloat(charge.amount || 0), 0);
    return cartTotal + serviceTotal + miscTotal;
  }, [cart, bookingData, miscCharges]);

  const getDiscount = useCallback(() => {
    if (!appliedCoupon) return 0;
    const subtotal = getSubtotal();
    if (appliedCoupon.discount_type === 'percentage') {
      return (subtotal * appliedCoupon.discount_value) / 100;
    } else {
      return Math.min(appliedCoupon.discount_value, subtotal);
    }
  }, [appliedCoupon, getSubtotal]);

  const getTotal = useCallback(() => {
    const subtotal = getSubtotal();
    const discount = getDiscount();
    return subtotal - discount;
  }, [getSubtotal, getDiscount]);

  const addToCart = useCallback((item, type = 'product', size = null) => {
    if (paymentConfirmed) {
      setPaymentConfirmed(false);
    }

    if (type === 'product' && !size && item.stock_by_size && Object.keys(item.stock_by_size).length > 0) {
      setSelectedProductForSize(item);
      setShowSizeModal(true);
      return;
    }

    const cartItemId = size ? `${item.id}-${size}` : item.id;
    const existingItem = cart.find(cartItem => cartItem.cartItemId === cartItemId && cartItem.type === type);

    if (type === 'service') {
      if (existingItem) {
        setCart(cart.map(cartItem =>
          cartItem.cartItemId === cartItemId && cartItem.type === type
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        ));
      } else {
        setCart([...cart, { ...item, quantity: 1, type: 'service', cartItemId: item.id }]);
      }
    } else {
      let availableStock = item.stock_level;
      if (size) {
        availableStock = item.stock_by_size[size] || 0;
      }

      if (availableStock <= 0) {
        handleError(null, `Product "${item.name}" ${size ? `(Size ${size})` : ''} is out of stock`);
        return;
      }

      if (existingItem) {
        if (existingItem.quantity < availableStock) {
          setCart(cart.map(cartItem =>
            cartItem.cartItemId === cartItemId && cartItem.type === type
              ? { ...cartItem, quantity: cartItem.quantity + 1 }
              : cartItem
          ));
        } else {
          handleError(null, 'Not enough stock available');
        }
      } else {
        setCart([...cart, { ...item, quantity: 1, type: 'product', size, cartItemId }]);
      }
    }
  }, [cart, paymentConfirmed]);

  const updateQuantity = useCallback((cartItemId, newQuantity, itemType = 'product') => {
    if (newQuantity === 0) {
      removeFromCart(cartItemId, itemType);
      return;
    }

    setCart(cart.map(item => {
      if (item.cartItemId === cartItemId && item.type === itemType) {
        if (itemType === 'product') {
          const product = products.find(p => p.id === item.id);
          let availableStock = product.stock_level;
          if (item.size && product.stock_by_size) {
            availableStock = product.stock_by_size[item.size] || 0;
          }
          if (newQuantity > availableStock) {
            handleError(null, 'Not enough stock available');
            return item;
          }
        }
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  }, [cart, products]);

  const updateItemPrice = useCallback((cartItemId, newPrice, itemType) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      handleError(null, 'Invalid price');
      return;
    }
    setCart(cart.map(item =>
      (item.cartItemId === cartItemId && item.type === itemType)
        ? { ...item, price: price }
        : item
    ));
  }, [cart]);

  const removeFromCart = useCallback((cartItemId, itemType = 'product') => {
    setCart(cart.filter(item => !(item.cartItemId === cartItemId && item.type === itemType)));
  }, [cart]);

  // Misc Charge Management
  const [showMiscChargeModal, setShowMiscChargeModal] = useState(false);
  const [miscChargeName, setMiscChargeName] = useState('');
  const [miscChargeAmount, setMiscChargeAmount] = useState('');

  const openMiscChargeModal = useCallback(() => {
    setMiscChargeName('');
    setMiscChargeAmount('');
    setShowMiscChargeModal(true);
  }, []);

  const addMiscCharge = useCallback(() => {
    if (!miscChargeName.trim()) {
      toast.error('Please enter a charge name');
      return;
    }
    if (!miscChargeAmount || isNaN(parseFloat(miscChargeAmount)) || parseFloat(miscChargeAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setMiscCharges(prev => [...prev, { name: miscChargeName.trim(), amount: parseFloat(miscChargeAmount) }]);
    toast.success(`Miscellaneous charge "${miscChargeName}" added`);
    setShowMiscChargeModal(false);
    setMiscChargeName('');
    setMiscChargeAmount('');
  }, [miscChargeName, miscChargeAmount]);

  const removeMiscCharge = useCallback((index) => {
    setMiscCharges(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Sync misc charges when booking is loaded
  useEffect(() => {
    if (bookingData && bookingData.misc_charges) {
      setMiscCharges(bookingData.misc_charges);
    } else if (!bookingData) {
      setMiscCharges([]);
    }
  }, [bookingData]);

  // Fetch transaction history
  const fetchTransactions = useCallback(async () => {
    setTxnLoading(true);
    try {
      const res = await apiGet(`${API_ENDPOINTS.POS_TRANSACTIONS}?limit=50`);
      setTransactions(res.transactions || res || []);
    } catch {
      toast.error('Failed to load transaction history');
    } finally {
      setTxnLoading(false);
    }
  }, []);

  const viewReceipt = useCallback(async (txn) => {
    try {
      const details = await apiGet(API_ENDPOINTS.POS_TRANSACTION_DETAILS(txn.id));
      setSelectedTransaction(details);
      setShowReceiptModal(true);
    } catch {
      setSelectedTransaction(txn);
      setShowReceiptModal(true);
    }
  }, []);

  const fetchCustomerHistory = useCallback(async (email) => {
    if (!email) return;
    setCustomerHistoryLoading(true);
    setShowCustomerHistory(true);
    try {
      const data = await apiGet(API_ENDPOINTS.CUSTOMER_HISTORY(email));
      setCustomerHistory(data);
    } catch {
      setCustomerHistory(null);
    } finally {
      setCustomerHistoryLoading(false);
    }
  }, []);

  const openRefundModal = useCallback((txn) => {
    setRefundTxn(txn);
    setRefundReason('');
    setShowRefundModal(true);
  }, []);

  const processRefund = useCallback(async () => {
    if (!refundTxn) return;
    if (!refundReason.trim()) {
      toast.error('Please provide a reason for the refund');
      return;
    }
    setRefundProcessing(true);
    try {
      await apiPost(API_ENDPOINTS.POS_TRANSACTION_REFUND(refundTxn.id), {
        reason: refundReason.trim(),
        refund_type: 'full'
      });
      toast.success(`Refund processed for ${refundTxn.transaction_number}`);
      setShowRefundModal(false);
      setShowReceiptModal(false);
      setSelectedTransaction(null);
      setRefundTxn(null);
      setRefundReason('');
      if (showHistoryPanel) fetchTransactions();
    } catch (err) {
      toast.error('Refund failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setRefundProcessing(false);
    }
  }, [refundTxn, refundReason, showHistoryPanel, fetchTransactions]);

  const handleSkuInput = useCallback((e) => {
    const sku = e.target.value;
    setSkuInput(sku);
    if (sku.length >= 3 && /^[\dA-Za-z]+$/.test(sku)) {
      searchProductBySku(sku);
    }
  }, []);

  const searchProductBySku = useCallback(async (sku) => {
    try {
      const response = await apiGet(API_ENDPOINTS.INVENTORY_BARCODE(sku));
      if (response) {
        const product = response;
        if (scanningMode) {
          addToCart(product);
          setSkuInput('');
          handleSuccess(`Product "${product.name}" added to cart!`);
        } else {
          setSearchTerm(product.name);
          handleSuccess(`Product "${product.name}" found!`);
        }
      }
    } catch (error) {
      console.error('Error searching product by SKU:', error);
      handleError(error, 'Product not found with this SKU');
    }
  }, [scanningMode, addToCart, handleSuccess, handleError]);

  const toggleScanningMode = useCallback(() => {
    setScanningMode(prev => !prev);
    setSkuInput('');
    if (!scanningMode) {
      setTimeout(() => {
        const skuInput = document.querySelector('input[placeholder="Scan or enter SKU..."]');
        if (skuInput) skuInput.focus();
      }, 100);
    }
  }, [scanningMode]);

  const applyCoupon = useCallback(async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      // Backend expects POST /coupons/validate with the code in the body
      const response = await apiPost('/coupons/validate', {
        code: couponCode,
        order_amount: getSubtotal()
      });
      setAppliedCoupon(response.coupon || response);
      handleSuccess('Coupon applied successfully!');
    } catch (error) {
      console.error('Error applying coupon:', error);
      handleError(error, 'Invalid or expired coupon code');
    } finally {
      setApplyingCoupon(false);
    }
  }, [couponCode, getSubtotal]);

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponCode('');
  }, []);

  const getPaymentStatusColor = useCallback((paymentStatus) => {
    switch (paymentStatus) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }, []);

  // ==========================================
  // PAYMENT HANDLERS (consolidated, fix #3)
  // ==========================================
  const managerEmail = user?.email || "manager@vonniesalon.com";

  const paystackConfig = useMemo(() => ({
    reference: generatePaystackReference(),
    email: managerEmail,
    amount: Math.round(getTotal() * 100),
    publicKey: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
    currency: "NGN",
    channels: ["card", "bank", "ussd", "qr", "mobile_money"],
    metadata: {
      booking_id: bookingData?.id,
      processed_by_user_id: user?.id,
      processed_by_email: managerEmail,
      processed_by_name: user?.name || user?.email,
      custom_fields: [
        { display_name: "Customer Name", variable_name: "customer_name", value: customerInfo.name },
        { display_name: "Customer Phone", variable_name: "customer_phone", value: customerInfo.phone },
        { display_name: "Booking Number", variable_name: "booking_number", value: bookingData?.booking_number || "Walk-in" },
        { display_name: "Transaction Type", variable_name: "transaction_type", value: bookingData ? "Booking + Products" : "Products Only" },
        { display_name: "Cart Items", variable_name: "cart_items", value: cart.map(item => `${item.name} x${item.quantity}`).join(", ") },
        { display_name: "Processed By", variable_name: "processed_by", value: user?.name || user?.email || "Manager" }
      ]
    }
  }), [getTotal, user, bookingData, customerInfo, cart, managerEmail]);

  const initializePayment = usePaystackPayment(paystackConfig);

  // Build transaction data (shared across all payment methods)
  const buildTransactionData = useCallback(() => {
    return {
      booking_number: bookingData?.booking_number || null,
      items: cart.map(item => ({
        product_id: item.type === 'product' ? item.id : null,
        service_id: item.type === 'service' ? item.id : null,
        quantity: item.quantity,
        price: item.price,
        type: item.type
      })),
      customer_info: customerInfo,
      staff_id: user?.id,
      coupon_code: appliedCoupon?.code || null,
      misc_charges: miscCharges
    };
  }, [bookingData, cart, customerInfo, user, appliedCoupon, miscCharges]);

  // Shared success handler for non-Paystack payments
  const handlePaymentSuccess = useCallback((responseData, paymentMethodLabel) => {
    setPaymentConfirmed(true);
    const transactionNumber = responseData.transaction_number || responseData.id || 'N/A';
    let successMessage = `${paymentMethodLabel} successful! Transaction ID: ${transactionNumber}`;

    if (bookingData) {
      successMessage += `\n\nBooking ${bookingData.booking_number} has been completed.`;
      successMessage += `\nService: ${bookingData.service_names ? bookingData.service_names.join(', ') : 'N/A'}`;
      successMessage += `\nTotal Amount: ₦${formatPrice(getTotal())}`;
    }

    handleSuccess(successMessage);
    setCart([]);
    setAppliedCoupon(null);
    setCouponCode('');
  }, [bookingData, getTotal]);

  // Generic payment processor for bank transfer / POS terminal
  const processOfflinePayment = useCallback(async (paymentMethod, paymentReferenceFn, paymentMethodLabel) => {
    if (!customerInfo.name || !customerInfo.phone) {
      toast.error("Please provide customer name and phone number");
      return;
    }
    if (getTotal() <= 0) {
      toast.error("Invalid payment amount");
      return;
    }
    setProcessing(true);
    try {
      const transactionData = {
        ...buildTransactionData(),
        payment_reference: paymentReferenceFn(),
        payment_method: paymentMethod,
        payment_status: 'completed'
      };

      const transactionResponse = await apiPost(API_ENDPOINTS.POS_CHECKOUT, transactionData);
      const responseData = transactionResponse.data || transactionResponse;
      const isSuccess = transactionResponse.success !== false && responseData;

      if (isSuccess) {
        handlePaymentSuccess(responseData, paymentMethodLabel);
      } else {
        handleError(null, 'Payment processing failed. Please try again.');
      }
    } catch (error) {
      console.error(`Error processing ${paymentMethodLabel}:`, error);
      handleError(error, `Error processing ${paymentMethodLabel}. Please try again.`);
    } finally {
      setProcessing(false);
    }
  }, [customerInfo, getTotal, buildTransactionData, handlePaymentSuccess]);

  // Paystack Payment
  const handlePaystackPayment = useCallback(() => {
    if (!customerInfo.name || !customerInfo.phone) {
      toast.error("Please provide customer name and phone number");
      return;
    }
    if (getTotal() <= 0) {
      toast.error("Invalid payment amount");
      return;
    }

    const onSuccess = async (response) => {
      try {
        setProcessing(true);
        const transactionData = {
          ...buildTransactionData(),
          payment_reference: response.reference,
          payment_method: 'paystack',
          payment_status: 'completed'
        };

        const transactionResponse = await apiPost(API_ENDPOINTS.POS_CHECKOUT, transactionData);
        const responseData = transactionResponse.data || transactionResponse;
        const isSuccess = transactionResponse.success !== false && responseData;

        if (isSuccess) {
          setPaymentConfirmed(true);
          toast.success("Payment processed and transaction completed successfully!");
          const transactionNumber = responseData.transaction_number || responseData.id || 'N/A';
          let successMessage = `Transaction completed! ID: ${transactionNumber}`;

          if (responseData.booking_updated && bookingData) {
            successMessage += `\n\nBooking ${bookingData.booking_number} updated to COMPLETED.`;
            successMessage += `\nService: ${bookingData.service_names ? bookingData.service_names.join(', ') : 'N/A'}`;
            successMessage += `\nTotal: ₦${formatPrice(getTotal())}`;
          }

          handleSuccess(successMessage);

          if (bookingData?.booking_number) {
            try {
              const refreshed = await fetchBookingData(bookingData.booking_number, apiGet, false);
              if (refreshed) setBookingData(refreshed);
            } catch { /* ignore refresh error */ }
          }

          clearCart();
          fetchProducts();
        } else {
          throw new Error("Transaction processing failed");
        }
      } catch (error) {
        console.error("Payment processing error:", error);
        toast.error("Payment processed but transaction failed. Please contact support.");
      } finally {
        setProcessing(false);
      }
    };

    const onClose = () => {
      toast.error("Payment cancelled by manager");
    };

    initializePayment(onSuccess, onClose);
  }, [customerInfo, getTotal, buildTransactionData, bookingData, initializePayment, clearCart, fetchProducts]);

  // Bank Transfer Payment
  const handleTransferPayment = useCallback(async () => {
    // Ask for the receipt/reference number from the transfer receipt or POS
    // terminal so the payment can be traced
    setReceiptModal({
      method: 'bank_transfer_pos',
      label: 'Bank transfer payment',
      title: 'Bank Transfer — Receipt / Reference Number',
      hint: 'Enter the receipt or reference number from the bank transfer or POS terminal slip.',
      value: ''
    });
  }, []);

  // POS Terminal Payment
  const handlePosTerminalPayment = useCallback(async () => {
    setReceiptModal({
      method: 'physical_pos',
      label: 'POS Terminal payment',
      title: 'POS Terminal — Receipt Number',
      hint: 'Enter the receipt number printed on the POS terminal slip.',
      value: ''
    });
  }, []);

  // Confirm the receipt number and record the payment
  const confirmReceiptPayment = useCallback(async () => {
    if (!receiptModal) return;
    const receipt = receiptModal.value.trim();
    if (receipt.length < 3) {
      toast.error('Please enter the receipt number from the POS slip or transfer receipt');
      return;
    }
    setReceiptModal(null);
    await processOfflinePayment(receiptModal.method, () => receipt, receiptModal.label);
  }, [receiptModal, processOfflinePayment]);

  // Card Payment (delegates to Paystack)
  const handleCardPayment = useCallback(() => {
    handlePaystackPayment();
  }, [handlePaystackPayment]);

  // Socket.IO Integration (fix #6 - proper dependency array)
  const bookingIdRef = useRef(null);
  useEffect(() => {
    bookingIdRef.current = bookingData?.id;
  }, [bookingData]);

  useEffect(() => {
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5057/api';
    const SOCKET_URL = API_URL.replace(/\/api$/, '');

    const socket = io(SOCKET_URL);

    socket.on('connect', () => {
      console.log('Connected to socket server');
    });

    socket.on('payment-verified', (data) => {
      if (bookingIdRef.current && data.booking_id === bookingIdRef.current && data.success) {
        handleSuccess(`Payment verified via Webhook! Reference: ${data.reference}`);
        setPaymentConfirmed(true);
        setCart([]);
        setAppliedCoupon(null);
        setCouponCode('');
        clearBooking();
        fetchProducts();
      }
    });

    socket.on('payment-failed', (data) => {
      if (bookingIdRef.current && data.booking_id === bookingIdRef.current) {
        handleError(null, `Payment failed via Webhook. Reference: ${data.reference}`);
      }
    });

    return () => {
      socket.off('payment-verified');
      socket.off('payment-failed');
      socket.disconnect();
    };
  }, [bookingData?.id, clearBooking, fetchProducts]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      <div className="flex flex-1 flex-col lg:flex-row overflow-hidden">
        {/* Main Content - Products/Services */}
        <div className="flex-1 p-4 lg:p-6 overflow-y-auto order-2 lg:order-1">
          <div className="max-w-7xl mx-auto space-y-4">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Point of Sale</h1>
                <p className="text-gray-500 mt-1 text-sm lg:text-base">Manage sales and transactions</p>
              </div>
              <button
                onClick={() => { setShowHistoryPanel(true); fetchTransactions(); }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Transaction History
              </button>
            </div>

            {apiOnline === false && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Server is unreachable. Please start the backend or check your connection.</span>
                  <span className="text-xs">API: {import.meta.env.VITE_API_URL}</span>
                </div>
              </div>
            )}

            {/* Booking Lookup */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Booking Lookup
              </h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  placeholder="Enter booking number (e.g., BK-JOHN-123)"
                  value={bookingNumber}
                  onChange={(e) => setBookingNumber(e.target.value.toUpperCase())}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                <div className="flex gap-2">
                  <button
                    onClick={fetchBooking}
                    disabled={bookingLoading}
                    className="flex-1 sm:flex-none px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm whitespace-nowrap"
                  >
                    {bookingLoading ? 'Loading...' : 'Find Booking'}
                  </button>
                  {bookingData && (
                    <button
                      onClick={clearBooking}
                      className="px-4 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {bookingData && (
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold text-blue-900 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Booking Details
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center">
                      <span className="text-gray-600 font-medium">Customer:</span>
                      <span className="ml-2 text-gray-900">{bookingData.customer_name}</span>
                      {bookingData.customer_email && (
                        <button
                          onClick={() => fetchCustomerHistory(bookingData.customer_email)}
                          className="ml-2 text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                        >
                          View History
                        </button>
                      )}
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-600 font-medium">Service:</span>
                      <span className="ml-2 text-gray-900">{bookingData.service_names ? bookingData.service_names.join(', ') : bookingData.service_name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-600 font-medium">Price:</span>
                      <span className="ml-2 text-gray-900 font-semibold">₦{formatPrice(bookingData.service_price || 0)}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-600 font-medium">Status:</span>
                      <span className={`ml-2 px-2.5 py-1 rounded-full text-xs font-medium ${bookingData.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                        bookingData.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                          bookingData.status === 'completed' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {bookingData.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    {bookingData.payment_status && (
                      <div className="flex items-center">
                        <span className="text-gray-600 font-medium">Payment:</span>
                        <span className={`ml-2 px-2.5 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(bookingData.payment_status)}`}>
                          {bookingData.payment_status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('products')}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${activeTab === 'products'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  📦 Products
                </button>
                <button
                  onClick={() => setActiveTab('services')}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${activeTab === 'services'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  🔧 Services
                </button>
              </div>
            </div>

            {/* Barcode Scanner */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  Barcode Scanner
                </h3>
                <button
                  onClick={toggleScanningMode}
                  className={`px-4 py-2 rounded-lg font-medium transition-all shadow-sm ${scanningMode
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                >
                  {scanningMode ? '⏹ Stop Scanning' : '▶ Start Scanning'}
                </button>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Scan or enter SKU..."
                  value={skuInput}
                  onChange={handleSkuInput}
                  className={`w-full px-4 py-3 pr-12 border-2 rounded-lg focus:outline-none focus:ring-2 transition ${scanningMode
                    ? 'border-green-300 focus:ring-green-500 bg-green-50'
                    : 'border-gray-300 focus:ring-blue-500 bg-white'
                    }`}
                />
                {scanningMode && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="flex items-center text-green-600">
                      <div className="animate-pulse w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {scanningMode
                  ? '✓ Scanning active - Products will auto-add to cart'
                  : 'Enter SKU to search. Toggle "Start Scanning" for auto-add mode.'
                }
              </p>
            </div>

            {/* Search and Filters */}
            {activeTab === 'products' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="🔍 Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {activeTab === 'services' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="🔍 Search services..."
                      value={serviceSearchTerm}
                      onChange={(e) => setServiceSearchTerm(e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    />
                    <select
                      value={selectedServiceCategory}
                      onChange={(e) => setSelectedServiceCategory(e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                    >
                      {serviceCategories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredServices.map(service => (
                    <div
                      key={service.id}
                      onClick={() => addToCart(service, 'service')}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center overflow-hidden border border-purple-200 group-hover:scale-105 transition flex-shrink-0">
                          {service.image_url || service.images?.[0]?.image_url ? (
                            <img
                              src={getFullImageUrl(service.image_url || service.images?.[0]?.image_url)}
                              alt={service.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xl">🔧</span>
                          )}
                        </div>
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                          {service.duration} min
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">{service.name}</h3>
                      <p className="text-xs text-gray-500 mb-3">{service.category}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-purple-600">₦{formatPrice(service.price)}</span>
                        <button className="w-8 h-8 bg-purple-600 text-white rounded-lg flex items-center justify-center hover:bg-purple-700 transition">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                      {service.description && (
                        <p className="text-xs text-gray-400 mt-2 line-clamp-2">{service.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Products Grid */}
            {activeTab === 'products' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    onClick={() => addToCart(product, 'product')}
                    className={`bg-white rounded-xl shadow-sm border p-4 hover:shadow-lg transition-all cursor-pointer group ${product.stock_level <= 0
                      ? 'border-red-200 opacity-60 cursor-not-allowed'
                      : product.stock_level <= 5
                        ? 'border-yellow-200 hover:border-yellow-300'
                        : 'border-gray-200 hover:border-blue-300'
                      }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center overflow-hidden border border-blue-200 group-hover:scale-105 transition flex-shrink-0">
                        {product.image_url || product.images?.[0]?.image_url ? (
                          <img
                            src={getFullImageUrl(product.image_url || product.images?.[0]?.image_url)}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xl">📦</span>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${product.stock_level <= 0
                        ? 'bg-red-100 text-red-700'
                        : product.stock_level <= 5
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                        {product.stock_level <= 0 ? 'Out of Stock' : `${product.stock_level} in stock`}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">{product.name}</h3>
                    <p className="text-xs text-gray-500 mb-3">{product.sku}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-blue-600">₦{formatPrice(product.price)}</span>
                      <button className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                    {product.description && (
                      <p className="text-xs text-gray-400 mt-2 line-clamp-2">{product.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cart Sidebar */}
        <div className="w-full lg:w-[400px] h-[300px] lg:h-auto bg-white border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col shadow-xl order-1 lg:order-2 overflow-hidden">
          <div className="p-4 flex flex-col h-full overflow-hidden">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Shopping Cart
              </h3>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                {cart.length + (bookingData ? 1 : 0)}
              </span>
            </div>

            {/* Customer Info */}
            <div className="mb-3 flex-shrink-0">
              <h4 className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">Customer Details</h4>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Customer Name *"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value.slice(0, 100) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-sm"
                />
                <input
                  type="tel"
                  placeholder="Phone Number *"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value.slice(0, 20) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-sm"
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value.slice(0, 255) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-sm"
                />
              </div>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto mb-2 -mx-2 px-2 space-y-2">
              {/* Miscellaneous Charges Section */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg mb-2">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs font-semibold text-gray-700">MISCELLANEOUS CHARGES</p>
                  <button
                    onClick={openMiscChargeModal}
                    className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700 transition shadow-sm"
                    title="Add miscellaneous charge"
                  >
                    + Add Charge
                  </button>
                </div>
                {miscCharges.length > 0 ? (
                  <div className="space-y-1.5">
                    {miscCharges.map((charge, idx) => (
                      <div key={idx} className="flex justify-between items-center text-xs text-gray-600 group">
                        <span className="truncate flex-1">- {charge.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">₦{formatPrice(charge.amount || 0)}</span>
                          <button
                            onClick={() => removeMiscCharge(idx)}
                            className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition p-1"
                            title="Remove charge"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-gray-400 italic">No miscellaneous charges added</p>
                )}
              </div>

              {cart.length === 0 && !bookingData ? (
                <div className="text-center py-2">
                  <svg className="w-10 h-10 mx-auto text-gray-300 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  <p className="text-gray-500 font-semibold text-xs">Your cart is empty</p>
                  <p className="text-gray-400 text-xs mt-0.5">Add products or services to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {bookingData && (
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center mb-1">
                            <span className="text-lg mr-2">📅</span>
                            <h4 className="font-semibold text-sm text-blue-900 truncate">{bookingData.service_names ? bookingData.service_names.join(', ') : bookingData.service_name || 'N/A'}</h4>
                          </div>
                          <p className="text-xs text-blue-600 font-medium">Booking: {bookingData.booking_number}</p>
                        </div>
                        <div className="ml-3 text-right">
                          <p className="text-sm font-bold text-blue-600">₦{formatPrice(bookingData.service_price || 0)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {cart.map(item => (
                    <div key={`${item.type}-${item.id}`} className="p-2 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 transition">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center mb-1">
                            <span className="text-sm mr-2">{item.type === 'service' ? '🔧' : '📦'}</span>
                            <h4 className="font-semibold text-sm text-gray-900 truncate">
                              {item.name} {item.size ? `(Size: ${item.size})` : ''}
                            </h4>
                          </div>
                          <div className="flex items-center space-x-1">
                            <p className="text-xs text-gray-500">₦{formatPrice(item.price)} each</p>
                            {item.type === 'service' && (
                              <button
                                onClick={() => {
                                  const newPrice = prompt("Enter new price:", item.price);
                                  if (newPrice !== null) {
                                    updateItemPrice(item.cartItemId || item.id, newPrice, item.type);
                                  }
                                }}
                                className="text-blue-500 hover:text-blue-700 p-1"
                                title="Edit Price"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            )}
                          </div>
                          {item.type === 'service' && (
                            <p className="text-xs text-purple-600 font-medium">{item.duration} minutes</p>
                          )}
                        </div>
                        <button
                          onClick={() => removeFromCart(item.cartItemId || item.id, item.type)}
                          className="ml-2 w-6 h-6 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition flex-shrink-0"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center bg-white border border-gray-300 rounded-lg overflow-hidden">
                          <button
                            onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity - 1, item.type)}
                            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 transition text-gray-600"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-gray-900">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity + 1, item.type)}
                            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 transition text-gray-600"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                        <p className="text-sm font-bold text-gray-900">₦{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Coupon and Payment Section */}
            <div className="space-y-3 flex-shrink-0 pt-2 border-t border-gray-100">
              {/* Coupon Section */}
              <div className="pb-3 border-b border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-sm disabled:bg-gray-100"
                    disabled={!!appliedCoupon || applyingCoupon}
                  />
                  {appliedCoupon ? (
                    <button
                      onClick={removeCoupon}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium text-sm"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={applyCoupon}
                      disabled={applyingCoupon}
                      className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-medium text-sm disabled:opacity-50"
                    >
                      {applyingCoupon ? '...' : 'Apply'}
                    </button>
                  )}
                </div>
                {appliedCoupon && (
                  <div className="mt-1 flex items-center text-sm">
                    <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-green-600 font-medium">Coupon &quot;{appliedCoupon.code}&quot; applied!</span>
                  </div>
                )}
              </div>

              {/* Total Section */}
              <div className="space-y-4">
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-medium">₦{formatPrice(getSubtotal())}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span className="font-medium">-₦{formatPrice(getDiscount())}</span>
                    </div>
                  )}
                </div>

                <div className="pt-2 border-t-2 border-gray-200">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-base font-bold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-blue-600">₦{formatPrice(getTotal())}</span>
                  </div>

                  {(bookingData || cart.length > 0) && !paymentConfirmed && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Online Payment</span>
                        {apiOnline ? (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Paystack ready</span>
                        ) : (
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">Paystack unavailable</span>
                        )}
                      </div>
                      <button
                        onClick={handlePaystackPayment}
                        disabled={processing || (cart.length === 0 && !bookingData) || !apiOnline}
                        className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all font-semibold text-sm shadow-lg disabled:shadow-none flex items-center justify-center gap-2 text-center"
                      >
                        {processing ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            <span>Processing...</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Pay with Paystack (Online)
                          </span>
                        )}
                      </button>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">or use when Paystack is down</span>
                        </div>
                      </div>

                      <button
                        onClick={handleTransferPayment}
                        disabled={processing || getTotal() <= 0}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border-2 border-blue-200"
                      >
                        {processing ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <span className="text-lg">🏦</span>
                            Bank Transfer / Physical POS
                          </span>
                        )}
                      </button>
                    </div>
                  )}

                  {paymentConfirmed && (
                    <div className="bg-green-100 text-green-800 px-3 py-2 rounded-md font-medium flex items-center justify-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Payment Confirmed
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* POS Receipt Number Modal (bank transfer / physical POS) */}
      {receiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">{receiptModal.title}</h3>
            <p className="text-xs text-gray-500 mb-4">{receiptModal.hint}</p>
            <input
              type="text"
              autoFocus
              value={receiptModal.value}
              onChange={(e) => setReceiptModal(prev => ({ ...prev, value: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmReceiptPayment(); }}
              placeholder="e.g. 0123456789"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setReceiptModal(null)}
                disabled={processing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReceiptPayment}
                disabled={processing || !receiptModal.value.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Size Selection Modal */}
      {showSizeModal && selectedProductForSize && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Select Size for {selectedProductForSize.name}</h3>
              <button
                onClick={() => { setShowSizeModal(false); setSelectedProductForSize(null); }}
                className="text-gray-400 hover:text-gray-600 transition"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {Object.entries(selectedProductForSize.stock_by_size || {})
                .filter(([_, stock]) => stock > 0)
                .map(([size, stock]) => (
                  <button
                    key={size}
                    onClick={() => {
                      addToCart(selectedProductForSize, 'product', size);
                      setShowSizeModal(false);
                      setSelectedProductForSize(null);
                    }}
                    className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-500 transition"
                  >
                    <span className="text-lg font-bold text-gray-800">{size}</span>
                    <span className="text-xs text-gray-500">{stock} available</span>
                  </button>
                ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => { setShowSizeModal(false); setSelectedProductForSize(null); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Misc Charge Modal */}
      {showMiscChargeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add Miscellaneous Charge</h3>
              <button
                onClick={() => setShowMiscChargeModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charge Name</label>
                <input
                  type="text"
                  placeholder="e.g., Delivery fee, Wrapping"
                  value={miscChargeName}
                  onChange={(e) => setMiscChargeName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₦)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={miscChargeAmount}
                  onChange={(e) => setMiscChargeAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowMiscChargeModal(false); setMiscChargeName(''); setMiscChargeAmount(''); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={addMiscCharge}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Add Charge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History Panel */}
      {showHistoryPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">Transaction History</h3>
              <button onClick={() => setShowHistoryPanel(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 border-b">
              <input
                type="text"
                placeholder="Search by customer name, transaction number..."
                value={txnSearch}
                onChange={(e) => setTxnSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {txnLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : transactions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No transactions found</p>
              ) : (
                <div className="space-y-2">
                  {transactions
                    .filter(t => {
                      if (!txnSearch) return true;
                      const q = txnSearch.toLowerCase();
                      return (
                        (t.customer_name || '').toLowerCase().includes(q) ||
                        (t.transaction_number || '').toLowerCase().includes(q) ||
                        (t.staff_name || '').toLowerCase().includes(q)
                      );
                    })
                    .map(txn => (
                      <div key={txn.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono font-bold text-blue-600">{txn.transaction_number}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              txn.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                              txn.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {txn.payment_status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 truncate">{txn.customer_name || 'Walk-in Customer'}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(txn.created_at).toLocaleString()} • {txn.staff_name || 'Unknown'}
                          </p>
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <p className="text-sm font-bold text-gray-900">₦{formatPrice(txn.total_amount)}</p>
                          <button
                            onClick={() => { viewReceipt(txn); setShowHistoryPanel(false); }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-1"
                          >
                            View Receipt
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setShowHistoryPanel(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Preview Modal */}
      {showReceiptModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">Receipt Preview</h3>
              <button onClick={() => { setShowReceiptModal(false); setSelectedTransaction(null); }} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6" id="receipt-content">
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Vonne X2X</h2>
                <p className="text-xs text-gray-500">Professional Service Management</p>
              </div>
              <div className="border-t border-b border-dashed border-gray-300 py-3 mb-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Transaction</span>
                  <span className="font-mono font-medium">{selectedTransaction.transaction_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Date</span>
                  <span>{new Date(selectedTransaction.created_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Customer</span>
                  <span>{selectedTransaction.customer_name || 'Walk-in'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Payment</span>
                  <span className="capitalize">{selectedTransaction.payment_method?.replace(/_/g, ' ')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Status</span>
                  <span className={`capitalize font-medium ${
                    selectedTransaction.payment_status === 'completed' ? 'text-green-600' :
                    selectedTransaction.payment_status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                  }`}>{selectedTransaction.payment_status}</span>
                </div>
              </div>
              {selectedTransaction.items && selectedTransaction.items.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Items</p>
                  {selectedTransaction.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1">
                      <span className="text-gray-700">
                        {item.product_name || item.service_name || 'Item'}
                        {item.quantity > 1 && ` x${item.quantity}`}
                      </span>
                      <span className="font-medium">₦{formatPrice(item.total_price)}</span>
                    </div>
                  ))}
                </div>
              )}
              {selectedTransaction.misc_charges && selectedTransaction.misc_charges.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Misc Charges</p>
                  {selectedTransaction.misc_charges.map((charge, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1">
                      <span className="text-gray-700">{charge.name}</span>
                      <span className="font-medium">₦{formatPrice(charge.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t border-dashed border-gray-300 pt-3 space-y-1">
                {selectedTransaction.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-₦{formatPrice(selectedTransaction.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-2">
                  <span>Total</span>
                  <span className="text-blue-600">₦{formatPrice(selectedTransaction.total_amount)}</span>
                </div>
              </div>
              <div className="text-center mt-6 pt-4 border-t border-dashed border-gray-300">
                <p className="text-xs text-gray-400">Thank you for your business!</p>
                <p className="text-xs text-gray-400 mt-1">Vonne X2X • vonneex2x.store</p>
              </div>
            </div>
            <div className="p-4 border-t space-y-2">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const printContent = document.getElementById('receipt-content');
                    if (printContent) {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`<html><head><title>Receipt - ${selectedTransaction.transaction_number}</title><style>body{font-family:monospace;padding:20px;max-width:400px;margin:0 auto;}table{width:100%;}</style></head><body>${printContent.innerHTML}</body></html>`);
                      printWindow.document.close();
                      printWindow.print();
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Receipt
                </button>
                <button
                  onClick={() => { setShowReceiptModal(false); setSelectedTransaction(null); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Close
                </button>
              </div>
              {selectedTransaction.status !== 'refunded' && selectedTransaction.payment_status !== 'refunded' && selectedTransaction.payment_status === 'completed' && (
                <button
                  onClick={() => openRefundModal(selectedTransaction)}
                  className="w-full px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Process Refund
                </button>
              )}
              {selectedTransaction.status !== 'cancelled' && selectedTransaction.payment_status === 'pending' && (
                <button
                  onClick={async () => {
                    if (window.confirm(`Are you sure you want to void / cancel transaction ${selectedTransaction.transaction_number}? Any deducted inventory stock will be immediately restored.`)) {
                      try {
                        await apiPost(API_ENDPOINTS.POS_TRANSACTION_CANCEL(selectedTransaction.id), { reason: 'Voided by cashier' });
                        toast.success('Transaction cancelled and inventory stock restored');
                        setShowReceiptModal(false);
                        setSelectedTransaction(null);
                        if (typeof fetchTransactions === 'function') fetchTransactions();
                      } catch (err) {
                        toast.error('Failed to cancel transaction: ' + (err.response?.data?.error || err.message));
                      }
                    }
                  }}
                  className="w-full px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Void / Cancel Pending Transaction
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Refund Confirmation Modal */}
      {showRefundModal && refundTxn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Confirm Refund</h3>
              <button onClick={() => { setShowRefundModal(false); setRefundTxn(null); setRefundReason(''); }} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                You are about to refund <strong>₦{formatPrice(refundTxn.total_amount)}</strong> for transaction <strong>{refundTxn.transaction_number}</strong>.
                Product stock will be restored automatically.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Refund Reason *</label>
              <textarea
                placeholder="Explain why this refund is being processed..."
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition text-sm"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowRefundModal(false); setRefundTxn(null); setRefundReason(''); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                disabled={refundProcessing}
              >
                Cancel
              </button>
              <button
                onClick={processRefund}
                disabled={refundProcessing || !refundReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {refundProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    Confirm Refund
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer History Panel */}
      {showCustomerHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">Customer History</h3>
              <button onClick={() => { setShowCustomerHistory(false); setCustomerHistory(null); }} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {customerHistoryLoading ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
              ) : !customerHistory ? (
                <p className="text-center text-gray-500 py-8">No customer history found</p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-600">{customerHistory.summary?.total_bookings || 0}</div>
                      <div className="text-xs text-blue-700">Bookings</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-green-600">{customerHistory.summary?.total_transactions || 0}</div>
                      <div className="text-xs text-green-700">Transactions</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-purple-600">₦{formatPrice(customerHistory.summary?.total_spent || 0)}</div>
                      <div className="text-xs text-purple-700">Total Spent</div>
                    </div>
                  </div>

                  {customerHistory.bookings?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent Bookings</h4>
                      <div className="space-y-1">
                        {customerHistory.bookings.slice(0, 5).map(b => (
                          <div key={b.id} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                            <div>
                              <span className="font-medium">{b.service_names || 'Service'}</span>
                              <span className="text-gray-400 ml-2">#{b.booking_number}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">{new Date(b.scheduled_time).toLocaleDateString()}</div>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                b.status === 'completed' ? 'bg-green-100 text-green-700' :
                                b.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>{b.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {customerHistory.transactions?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent POS Transactions</h4>
                      <div className="space-y-1">
                        {customerHistory.transactions.slice(0, 5).map(t => (
                          <div key={t.id} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                            <div>
                              <span className="font-mono text-xs text-blue-600">{t.transaction_number}</span>
                              <span className="text-gray-400 ml-2 capitalize">{t.payment_method?.replace(/_/g, ' ')}</span>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">₦{formatPrice(t.total_amount)}</div>
                              <div className="text-xs text-gray-500">{new Date(t.created_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end">
              <button onClick={() => { setShowCustomerHistory(false); setCustomerHistory(null); }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POS;